import React from 'react';
import { connect } from 'react-redux';
import { Tabs, Tab, Typography, GridLegacy as Grid } from '@mui/material';
import { parseStringToInt } from '../../utils/parsing';
import getSearchParams, { updateCurrentPath } from '../../utils/url';
import { AUTH_LEVELS } from '../auth/Authorized';
import BuildingsLoader from '../loaders/BuildingsLoader';
import OrganizationsLoader from '../loaders/OrganizationsLoader';
import EquipTypeLoader from '../loaders/EquipTypeLoader';
import UnitsLoader from '../loaders/UnitsLoader';
import PointTypeLoader from '../loaders/PointTypeLoader';
import MeasurementsLoader from '../loaders/MeasurementsLoader';
import PointsView from './views/PointsView';
import DevicesView from './views/DevicesView';
import EquipmentView from './views/EquipmentView';
import OverviewView from './views/OverviewView';
import LocationView from './views/LocationView';
import RelationshipView from './views/RelationshipView';
import { Page, Row, Fill } from '../util/Page';
import ChangelogFilters from '../util/ChangelogFilters';
import useDatedBuildings from '../util/dated-buildings';
import { filterBuildings } from '../../redux/selectors/buildings';
import ProcessingAlert from './elements/ProcessingAlert';

const Staging = ({
  isSuperAdmin,
  orgId,
  buildingId,
  buildingsById,
  navigate,
  view = isSuperAdmin ? 'Overview' : 'Point',
}) => {
  // First is the default
  const VIEWS = {
    Overview: OverviewView,
    Devices: DevicesView,
    Point: PointsView,
    Equipment: EquipmentView,
    Locations: LocationView,
    Relationships: RelationshipView,
  };

  const VIEW_KEYS = Object.keys(VIEWS);

  const [filters, setFilters] = useDatedBuildings({ orgId, buildingId });
  const buildings = filterBuildings(buildingsById, filters);
  const bid = buildings.length === 1 ? buildings[0] : null;
  const buildingName = buildingsById[bid]?.name;

  const setUrlAndFilters = (newFilters) => {
    const newBid = newFilters.buildings.length === 1 ? newFilters.buildings[0] : '';
    updateCurrentPath(navigate, { org: newFilters.orgId, bid: newBid });
    setFilters(newFilters);
  };

  return (
    <div>
      <Grid container>
        <ProcessingAlert buildingId={buildingId} />

        <OrganizationsLoader />
        <BuildingsLoader />
        <MeasurementsLoader />
        <UnitsLoader />
        <PointTypeLoader />
        <EquipTypeLoader />

        <Grid item xs={12}>
          <Page name="dashboard">
            <Row>
              <Typography variant="h6">Staging{buildingName && ` - ${buildingName}`}</Typography>
              <Fill />
              <ChangelogFilters
                filters={filters}
                setFilters={setUrlAndFilters}
                noDates
                singleBuilding
              />
            </Row>
            <Row>
              <Tabs
                indicatorColor="secondary"
                textColor="inherit"
                value={VIEW_KEYS.indexOf(view) < 0 ? 0 : VIEW_KEYS.indexOf(view)}
                onChange={(_event, val) => {
                  updateCurrentPath(navigate, { view: VIEW_KEYS[val] });
                }}
              >
                {VIEW_KEYS.map((t) => (
                  <Tab key={t} label={t} />
                ))}
              </Tabs>
            </Row>
          </Page>
        </Grid>
        {React.createElement(VIEWS[view] ?? VIEWS[VIEW_KEYS[0]], { buildingId })}
      </Grid>
    </div>
  );
};

export default connect(
  (
    {
      entities: {
        buildings: { byId },
      },
      user: { userAuthLevel },
    },
    { location: { search } }
  ) => {
    const { org, bid, view } = getSearchParams(search);
    return {
      orgId: parseStringToInt(org),
      buildingId: parseStringToInt(bid),
      buildingsById: byId,
      canCreateBuilding: userAuthLevel === AUTH_LEVELS.SUPER_ADMIN,
      isSuperAdmin: userAuthLevel === AUTH_LEVELS.SUPER_ADMIN,
      view,
    };
  }
)(Staging);
